import { ENUM_LABELS, type ExpenseRow, formatPersonName } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';
import { ResourceListPage } from '#client/components/ui/ResourceListPage';
import { dash, formatDate, formatMoney } from '#client/lib/format';
import { RESOURCE } from '#client/lib/routes';

const columns: Column<ExpenseRow>[] = [
  {
    key: 'incurred_on',
    header: 'Date',
    width: '130px',
    frozen: true,
    // The day it was incurred, not the day it was keyed in — a month's rent
    // entered late still belongs where it happened, and the P&L sums on this.
    render: (row) => <span className="tnum">{formatDate(row.incurred_on)}</span>,
  },
  { key: 'description', header: 'Description', render: (row) => row.description },
  {
    key: 'category',
    header: 'Category',
    width: '190px',
    render: (row) => (
      <span>
        {row.category.name}{' '}
        <Badge tone="neutral">{ENUM_LABELS.expenseKind[row.category.kind]}</Badge>
      </span>
    ),
  },
  {
    key: 'vendor',
    header: 'Paid to',
    width: '170px',
    render: (row) => dash(row.vendor),
  },
  {
    key: 'amount',
    header: 'Amount',
    width: '150px',
    align: 'end',
    render: (row) => <span className="tnum">{formatMoney(row.amount)}</span>,
  },
  {
    key: 'user',
    header: 'Entered by',
    width: '160px',
    render: (row) => formatPersonName(row.user),
  },
];

/**
 * Money out.
 *
 * Reagent consumption is deliberately not here: it is derived from the stock
 * ledger, and the seeded category list has no reagent entry so the same rupee
 * cannot be counted twice. Everything on this screen is a cost somebody typed —
 * salaries, rent, diesel, a new centrifuge.
 */
export function ExpenseListPage() {
  return (
    <ResourceListPage<ExpenseRow>
      title="Expenses"
      subtitle="Salaries, rent, running costs and equipment. Reagent cost comes from the stock ledger."
      path={RESOURCE.expenses}
      columns={columns}
      noun="expense"
      nounPlural="expenses"
      searchPlaceholder="Search description, vendor or reference"
      rowLabel={(row) => `${row.description} — ${formatMoney(row.amount)}`}
      createCapability="expense.create"
      editCapability="expense.edit"
      deleteCapability="expense.delete"
      emptyBody="Record what the lab spends — salaries, rent, electricity, repairs — and the profit-and-loss statement starts meaning something."
    />
  );
}
